import { useId, useState, type FormEvent } from 'react';
import { Button, Field, Input, Modal } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/use-auth';
import type { MarketingEmailAccount } from '@/lib/types';
import { SMTP_FAILED_CODE } from './email-account-rules';

export interface EmailAccountTestDialogProps {
  account: MarketingEmailAccount | null;
  onClose: () => void;
  /** A test ran (either way) — the row's "last test" moved, so re-read. */
  onTested: () => void;
}

type Outcome =
  | { kind: 'sent'; to: string }
  | { kind: 'failed'; message: string; reasons: string[] };

/**
 * Sends one test message through the account's SMTP settings, synchronously.
 *
 * ⚠️ **A success means the relay ACCEPTED the message, not that it arrived** —
 * nothing on either side sees spam filtering or a later bounce, so the copy
 * says "check that it arrives" rather than "delivered".
 *
 * ⚠️ **On `502 MARKETING_SMTP_FAILED` the relay's own reason is in
 * `details[].reason`** and is rendered BESIDE the message, verbatim — "535
 * Username and Password not accepted" and "connect ETIMEDOUT" send somebody to
 * opposite fixes, and a house sentence in their place leaves no next step.
 */
export function EmailAccountTestDialog({ account, onClose, onTested }: EmailAccountTestDialogProps) {
  if (account === null) return null;
  return <TestDialog key={account.id} account={account} onClose={onClose} onTested={onTested} />;
}

function TestDialog({
  account,
  onClose,
  onTested,
}: {
  account: MarketingEmailAccount;
  onClose: () => void;
  onTested: () => void;
}) {
  const formId = useId();
  const { user } = useAuth();
  const [to, setTo] = useState(user?.email ?? '');
  const [sending, setSending] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    event.stopPropagation();
    const recipient = to.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient)) {
      setFieldError('Enter a valid email address.');
      return;
    }
    setFieldError(null);
    setOutcome(null);
    setSending(true);
    try {
      await api.testMarketingEmailAccount(account.id, recipient);
      setOutcome({ kind: 'sent', to: recipient });
      onTested();
    } catch (caught) {
      if (caught instanceof ApiError) {
        const toError = caught.fieldErrors.to;
        if (toError) {
          setFieldError(toError);
        } else {
          setOutcome({
            kind: 'failed',
            message:
              caught.code === SMTP_FAILED_CODE
                ? caught.message || 'The mail server refused the test message.'
                : caught.formMessage ?? caught.message,
            reasons: caught.detailReasons,
          });
          if (caught.code === SMTP_FAILED_CODE) onTested();
        }
      } else {
        setOutcome({
          kind: 'failed',
          message: 'Could not reach the server. No test message was sent.',
          reasons: [],
        });
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={`Send a test from ${account.name}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={sending}>
            Close
          </Button>
          <Button type="submit" form={formId} variant="primary" loading={sending}>
            Send test
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="grid gap-4">
        <p className="m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
          From {account.fromName} &lt;<EmailLink address={account.fromEmail} tone="muted" />&gt; via {account.smtpHost}:{account.smtpPort}
        </p>
        <Field label="Send to" required error={fieldError}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              type="email"
              value={to}
              autoComplete="email"
              invalid={Boolean(fieldError)}
              aria-describedby={describedBy}
              onChange={(event) => setTo(event.target.value)}
            />
          )}
        </Field>

        {outcome?.kind === 'sent' && (
          <Notice tone="success" role="status" className="[overflow-wrap:anywhere]">
            The mail server accepted the message for <EmailLink address={outcome.to} />.
          </Notice>
        )}
        {outcome?.kind === 'failed' && (
          <Notice tone="danger" role="alert" title={<span className="[overflow-wrap:anywhere]">{outcome.message}</span>}>
            {outcome.reasons.length > 0 && (
              <div className="grid gap-1">
                {outcome.reasons.map((reason) => (
                  <code key={reason} className="block [overflow-wrap:anywhere]">
                    {reason}
                  </code>
                ))}
              </div>
            )}
          </Notice>
        )}
      </form>
    </Modal>
  );
}
